import { type Language } from "@ap/i18n";
import { AppProviders, ErrorBoundary, useAuth } from "@ap/ui-kit";
import { Result, Spin } from "antd";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { AppLayout } from "./layout/AppLayout";
import { roleLabel } from "./lib/roleLabel";
import { ApiProvider } from "./lib/api";
import { ROUTES, routeOf } from "./routes";

const ConnectSystem = lazy(() => import("./features/connect/ConnectPage"));

/** Roles only the platform team grants (to people of the Platform team). */
const PLATFORM_GRANTED = new Set(["platform-admin", "platform-auditor", "tool-admin", "code-runner", "marketplace-curator"]);

/** Access denied, with the roles that open the page and who can give them (coherence finding 7). */
function Forbidden({ roles }: { roles: string[] }) {
  const { t } = useTranslation("shell");
  const ta = useTranslation("admin").t;
  const names = roles.map((r) => roleLabel(ta, r));
  const platformOnly = roles.length > 0 && roles.every((r) => PLATFORM_GRANTED.has(r));
  return (
    <Result
      status="403"
      title={t("forbidden.title")}
      subTitle={
        <div data-testid="forbidden-help">
          <p>{names.length ? t("forbidden.needs", { roles: names.join(t("forbidden.sep")) }) : t("forbidden.body")}</p>
          {names.length ? <p>{t(platformOnly ? "forbidden.askPlatform" : "forbidden.askOrg")}</p> : null}
        </div>
      }
    />
  );
}

function NotFound() {
  const { t } = useTranslation("shell");
  return <Result status="404" title={t("notFound")} />;
}

/** Moves focus to <main> and announces the page title on navigation (WCAG 2.4.3). */
function RouteFocus() {
  const loc = useLocation();
  const { t } = useTranslation("shell");
  useEffect(() => {
    const r = routeOf(loc.pathname);
    document.title = r ? `${t(r.label)} · ${t("product")}` : t("product");
    document.getElementById("main")?.focus({ preventScroll: true });
  }, [loc.pathname, t]);
  return null;
}

function Guard({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { user } = useAuth();
  const ok = roles.length === 0 || roles.some((r) => user?.roles.includes(r));
  return ok ? <>{children}</> : <Forbidden roles={roles} />;
}

export function App({ lang, onLanguage }: { lang: Language; onLanguage: (l: Language) => void }) {
  return (
    <AppProviders lang={lang}>
      <BrowserRouter>
        <ApiProvider>
          <RouteFocus />
          <AppLayout lang={lang} onLanguage={onLanguage}>
            <ErrorBoundary>
              <Suspense fallback={<Spin size="large" style={{ display: "block", marginBlock: 64 }} />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/home" replace />} />
                  <Route path="/admin/tools/connect" element={<Guard roles={["tool-admin", "platform-admin"]}><ConnectSystem /></Guard>} />
                  {ROUTES.map(({ key, path, roles, Component }) => (
                    <Route
                      key={key}
                      path={`${path}/*`}
                      element={
                        <Guard roles={roles}>
                          <Component />
                        </Guard>
                      }
                    />
                  ))}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </AppLayout>
        </ApiProvider>
      </BrowserRouter>
    </AppProviders>
  );
}
